/**
 * Add-on strip of the build_your_routine block ("Complete your Smyle ritual").
 *
 * - Keeps each subscription-capable add-on's hidden selling-plan input in step with
 *   its checkbox and its Once / Subscribe choice: the plan is only posted while the
 *   add-on is ticked and Subscribe is chosen (Once is the default).
 * - Sums the ticked add-ons, plus the main line when its tile is shown, into the
 *   footer total, and the time of timed stops into the header pill.
 * - Follows the main colour picker so the first tile shows the chosen variant.
 *
 * The main line's share comes from the selected pack of the offer selector / duo
 * pack when present, else from the theme's live price object
 * (window.purchaseOptions.discounted_price.current), else the variant price.
 */
if (!customElements.get('build-routine')) {
  class BuildRoutine extends HTMLElement {
    connectedCallback() {
      this.inputs = Array.from(this.querySelectorAll('.build-routine__input'));
      this.modes = Array.from(this.querySelectorAll('.build-routine__mode'));
      this.totalEl = this.querySelector('.js-routine-total');
      this.totalWrap = this.querySelector('.build-routine__total');
      this.timeEl = this.querySelector('.js-routine-time');
      this.anchorImage = this.querySelector('.js-routine-anchor-image');
      this.includeAnchor = this.dataset.includeAnchor !== 'false';
      this.anchorPrice = parseInt(this.dataset.anchorPrice, 10) || 0;
      if (this.anchorImage) {
        this.anchorImage.dataset.defaultSrc = this.anchorImage.getAttribute('src') || '';
        this.anchorImage.dataset.defaultSrcset = this.anchorImage.getAttribute('srcset') || '';
      }

      this.onInputChange = this.onInputChange.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onPickerChange = this.onPickerChange.bind(this);
      this.refresh = this.refresh.bind(this);
      this.render = this.render.bind(this);

      this.inputs.forEach((input) => input.addEventListener('change', this.onInputChange));
      this.modes.forEach((mode) => mode.addEventListener('change', this.onModeChange));
      this.querySelectorAll('details.build-routine__info').forEach((details) => {
        details.addEventListener('toggle', () => this.positionPopover(details));
      });

      const formId = this.dataset.formId || '';
      const section = this.closest('.js-product') || document;
      this.mainPicker = section.querySelector(`variant-picker[data-form-id="${formId}"]:not([data-simple-mode])`);
      if (this.mainPicker) this.mainPicker.addEventListener('change', this.onPickerChange);

      // A pack chooser (offer selector or duo pack) prices one or two brushes as a
      // set; when present its selected pack is the main line's share of the total.
      this.offerEl = section.querySelector('product-offer, product-duo');
      if (this.offerEl) this.offerEl.addEventListener('change', this.render);

      // The theme recomputes the main line's price on quantity, pack and
      // subscription changes and announces it with this event.
      window.addEventListener('on:price:changed', this.render);

      // Back/forward restores checkbox and radio state but not the server-rendered
      // disabled attribute of the hidden plan input, so re-sync on every show.
      window.addEventListener('pageshow', this.refresh);

      if (this.mainPicker && this.anchorImage) this.onPickerChange();
      this.refresh();
    }

    disconnectedCallback() {
      if (this.mainPicker) this.mainPicker.removeEventListener('change', this.onPickerChange);
      if (this.offerEl) this.offerEl.removeEventListener('change', this.render);
      window.removeEventListener('on:price:changed', this.render);
      window.removeEventListener('pageshow', this.refresh);
    }

    /**
     * Ticking an add-on that offers a subscription selects the subscription, which
     * is the better-value option, unless the shopper has already picked one
     * themselves on that card.
     */
    onInputChange(event) {
      const input = event && event.target;
      const tile = input && input.closest('.build-routine__tile');
      if (input && input.checked && tile && tile.dataset.modeTouched !== 'true') {
        const planMode = tile.querySelector('.build-routine__mode[value="plan"]');
        if (planMode && !planMode.checked) planMode.checked = true;
      }
      this.refresh();
    }

    /** Picking Once or Subscribe is a clear intent to add the item. */
    onModeChange(event) {
      const tile = event.target.closest('.build-routine__tile');
      if (tile) tile.dataset.modeTouched = 'true';
      const input = tile && tile.querySelector('.build-routine__input');
      if (input && !input.checked) input.checked = true;
      this.refresh();
    }

    refresh() {
      this.syncTiles();
      this.render();
    }

    /**
     * Keeps an open info popover inside the viewport: it is centred on its tile,
     * which would otherwise run off-screen for the tiles nearest an edge.
     */
    positionPopover(details) {
      const body = details.querySelector('.build-routine__info-body');
      if (!body || !details.open) return;

      body.style.transform = 'translateX(-50%)';
      const rect = body.getBoundingClientRect();
      const min = 8;
      const max = document.documentElement.clientWidth - 8;
      let shift = 0;
      if (rect.left < min) shift = min - rect.left;
      else if (rect.right > max) shift = max - rect.right;
      if (shift) body.style.transform = `translateX(calc(-50% + ${Math.round(shift)}px))`;
    }

    /** True when this add-on should be posted on its selling plan. */
    isSubscribed(input) {
      const tile = input.closest('.build-routine__tile');
      if (!tile) return false;
      const planMode = tile.querySelector('.build-routine__mode[value="plan"]');
      if (planMode) return planMode.checked;
      // Plan-only variants have a plan input and no choice.
      return !!tile.querySelector('input[name$="[selling_plan]"]');
    }

    priceFor(input) {
      if (input.dataset.pricePlan && input.dataset.priceOnce) {
        return parseInt(this.isSubscribed(input) ? input.dataset.pricePlan : input.dataset.priceOnce, 10) || 0;
      }
      return parseInt(input.dataset.price, 10) || 0;
    }

    /** Plan inputs, the lit repeat badge and the price shown in each card. */
    syncTiles() {
      const canFormat = window.Shopify && typeof window.Shopify.formatMoney === 'function';

      this.inputs.forEach((input) => {
        const tile = input.closest('.build-routine__tile');
        if (!tile) return;
        const subscribed = this.isSubscribed(input);

        const plan = tile.querySelector('input[name$="[selling_plan]"]');
        if (plan) plan.disabled = !(input.checked && subscribed);

        tile.classList.toggle('is-selected', input.checked);
        tile.classList.toggle('is-subscribed', input.checked && subscribed);
        // The saving only applies to the plan, so the pill follows the chosen mode
        // rather than the tick: it stays hidden while Once is selected.
        tile.classList.toggle('is-plan-mode', subscribed);

        const priceEl = tile.querySelector('.js-slot-price');
        if (priceEl && canFormat && input.dataset.priceOnce) {
          priceEl.textContent = window.Shopify.formatMoney(this.priceFor(input));
        }

        // The struck price is the one-time price, shown only while the cheaper
        // subscription is selected; a one-time pick never looks discounted.
        const compareEl = tile.querySelector('.js-slot-compare');
        if (compareEl) {
          const compare = parseInt(compareEl.dataset.compare, 10) || 0;
          compareEl.hidden = !(subscribed && compare > this.priceFor(input));
        }
      });
    }

    /**
     * Resolves the currently selected variant of a variant-picker from its embedded
     * variant JSON and checked options (same approach as the duo pack).
     */
    getPickerVariant(pickerEl) {
      if (!pickerEl) return null;

      let variants = pickerEl._routineVariants;
      if (!variants) {
        const dataEl = pickerEl.querySelector('script[type="application/json"]');
        if (!dataEl) return null;
        try {
          variants = JSON.parse(dataEl.textContent).variants;
        } catch (e) {
          return null;
        }
        pickerEl._routineVariants = variants;
      }

      const selected = [];
      pickerEl.querySelectorAll('.option-selector').forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const opt = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selected.push(opt && opt.dataset.value !== '' ? opt.dataset.value : null);
        } else {
          const opt = selector.querySelector('input:checked');
          selected.push(opt ? opt.value : null);
        }
      });

      return variants.find((v) => v.options.every((val, i) => val === selected[i])) || null;
    }

    onPickerChange() {
      const variant = this.getPickerVariant(this.mainPicker);
      if (!variant) return;

      this.anchorPrice = variant.price;

      if (this.anchorImage) {
        const image = variant.featured_image && variant.featured_image.src;
        if (image) {
          this.anchorImage.src = image.includes('?') ? `${image}&width=240` : `${image}?width=240`;
          this.anchorImage.srcset = '';
        } else {
          // A colour without its own image falls back to the product image the
          // server rendered, so the tile never keeps a previous colour.
          this.anchorImage.src = this.anchorImage.dataset.defaultSrc;
          this.anchorImage.srcset = this.anchorImage.dataset.defaultSrcset;
        }
      }

      // The pack chooser recomputes its own prices on the same picker event; render
      // after it so the main line's share reflects the new colour.
      window.requestAnimationFrame(this.render);
    }

    /** Selected pack price from the offer selector / duo pack, or null without one. */
    packPrice() {
      if (!this.offerEl) return null;
      const checked = this.offerEl.querySelector('input.js-option-tab:checked');
      const offer = checked && checked.value === '1' ? 'duo' : 'one';
      const priceEl = this.offerEl.querySelector(`.js-price[data-offer="${offer}"]`);
      if (!priceEl) return null;
      const po = window.purchaseOptions || {};
      const key = po.purchase_type === 'subscription' ? 'subscriptionPrice' : 'onetimePrice';
      const value = parseInt(priceEl.dataset[key], 10);
      return Number.isFinite(value) && value > 0 ? value : null;
    }

    /** "m:ss" or "ss" to seconds; anything else counts as zero. */
    parseTime(text) {
      const parts = String(text || '').trim().split(':').map((p) => parseInt(p, 10));
      if (parts.some((p) => Number.isNaN(p))) return 0;
      if (parts.length === 1) return parts[0];
      return parts[0] * 60 + parts[1];
    }

    formatTime(seconds) {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    }

    /** The main line's live price when the theme has computed one, else the variant price. */
    anchorTotal() {
      const pack = this.packPrice();
      if (pack !== null) return pack;
      const po = window.purchaseOptions;
      const live = po && po.discounted_price && po.discounted_price.current;
      return Number.isFinite(live) && live > 0 ? live : this.anchorPrice;
    }

    render() {
      let total = this.includeAnchor ? this.anchorTotal() : 0;
      let seconds = this.parseTime(this.dataset.anchorTime);

      this.inputs.forEach((input) => {
        if (!input.checked) return;
        total += this.priceFor(input);
        seconds += this.parseTime(input.dataset.time);
      });

      if (this.totalEl && window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        this.totalEl.textContent = window.Shopify.formatMoney(total);
      }
      // Without the main line in the total, an empty selection shows no total.
      if (this.totalWrap) this.totalWrap.hidden = !this.includeAnchor && total === 0;
      if (this.timeEl) this.timeEl.textContent = this.formatTime(seconds);
    }
  }

  customElements.define('build-routine', BuildRoutine);
}
